import { Component } from '@theme/component';
import {
  debounce,
  isClickedOutside,
  lockScroll,
  onAnimationEnd,
  unlockScroll,
} from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** Scroll position captured immediately before opening. */
  #previousScrollY = 0;

  /** Pending animation frame used to open the native dialog. */
  #openAnimationFrame = null;

  /** Whether this component currently owns the shared root scroll lock. */
  #ownsScrollLock = false;

  /** Prevents duplicate asynchronous close operations. */
  #isClosing = false;

  /** Ensures each open event receives at most one close event. */
  #isActive = false;

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this.refs;

    dialog.addEventListener('cancel', this.#handleNativeCancel);
    dialog.addEventListener('close', this.#handleNativeClose);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { dialog } = this.refs;

    this.#cancelPendingOpen();

    dialog.removeEventListener('cancel', this.#handleNativeCancel);
    dialog.removeEventListener('close', this.#handleNativeClose);

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    this.#finalizeClosedState({
      restoreScroll: false,
      dispatchClose: false,
    });
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;

    if (windowWidth < minWidth || windowWidth > maxWidth) {
      void this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (
      dialog.open ||
      this.#openAnimationFrame !== null ||
      this.#isClosing
    ) {
      return;
    }

    this.#previousScrollY = getScrollTop();

    // Keep DOM reads and writes in separate frames while retaining a
    // cancellable reference in case the component closes or disconnects.
    this.#openAnimationFrame = requestAnimationFrame(() => {
      this.#openAnimationFrame = null;

      if (
        !this.isConnected ||
        dialog.open ||
        this.#isClosing
      ) {
        return;
      }

      lockScroll(dialog);
      this.#ownsScrollLock = true;

      try {
        dialog.showModal();
      } catch (error) {
        // Do not leave the page locked if the browser rejects showModal().
        this.#releaseScrollLock(false);

        // Preserve visibility of unexpected implementation errors.
        console.error('Unable to open dialog.', error);
        return;
      }

      this.#isActive = true;

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   *
   * Cleanup that affects page interaction runs before the closing animation,
   * ensuring a suspended or cancelled animation cannot retain scroll-lock.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    this.#cancelPendingOpen();

    if (this.#isClosing) return;

    if (!dialog.open) {
      this.#finalizeClosedState();
      return;
    }

    this.#isClosing = true;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force the browser to restart the closing animation.
    dialog.style.animation = 'none';

    // Force a reflow.
    void dialog.offsetWidth;

    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    // Release the root lock and restore the page before waiting for animation.
    this.#releaseScrollLock(true);

    // The timeout ensures a suspended animation cannot block final cleanup.
    await Promise.race([
      onAnimationEnd(dialog, undefined, {
        subtree: false,
      }),
      new Promise((resolve) => {
        window.setTimeout(resolve, 500);
      }),
    ]);

    if (dialog.open) {
      dialog.close();
    }

    this.#finalizeClosedState({
      restoreScroll: false,
    });
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      void this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Cancels a scheduled dialog-open frame.
   */
  #cancelPendingOpen() {
    if (this.#openAnimationFrame === null) return;

    cancelAnimationFrame(this.#openAnimationFrame);
    this.#openAnimationFrame = null;
  }

  /**
   * Releases this component's root scroll lock.
   *
   * @param {boolean} [restoreScroll=true] Whether to restore the captured
   * scroll position.
   */
  #releaseScrollLock(restoreScroll = true) {
    if (!this.#ownsScrollLock) return;

    unlockScroll(this.refs.dialog);
    this.#ownsScrollLock = false;

    if (restoreScroll) {
      scrollTo({
        top: this.#previousScrollY,
        behavior: 'instant',
      });
    }
  }

  /**
   * Synchronizes component state after either a controlled or native close.
   *
   * The method is idempotent: repeated calls do not restore scroll or
   * dispatch the close event more than once.
   *
   * @param {object} [options]
   * @param {boolean} [options.restoreScroll=true]
   * @param {boolean} [options.dispatchClose=true]
   */
  #finalizeClosedState({
    restoreScroll = true,
    dispatchClose = true,
  } = {}) {
    const { dialog } = this.refs;
    const shouldDispatchClose = dispatchClose && this.#isActive;

    this.#cancelPendingOpen();

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    this.#releaseScrollLock(restoreScroll);

    dialog.classList.remove('dialog-closing');
    dialog.style.animation = '';

    this.#isClosing = false;
    this.#isActive = false;

    if (shouldDispatchClose) {
      this.dispatchEvent(new DialogCloseEvent());
    }
  }

  /**
   * Handles native dialog cancellation, including supported mobile
   * browser Back gestures/buttons.
   *
   * Preventing the default native dismissal ensures the controlled close
   * path can run its animation and cleanup.
   *
   * @param {Event} event
   */
  #handleNativeCancel = (event) => {
    event.preventDefault();
    void this.closeDialog();
  };

  /**
   * Handles a native dialog close that occurs outside closeDialog().
   */
  #handleNativeClose = () => {
    const { dialog } = this.refs;

    // Ignore a stale close event if another operation already reopened it.
    if (dialog.open) return;

    this.#finalizeClosedState();
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      void this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses Escape.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    void this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number}
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number}
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);